// ── Shared refresh cycle (P11b) ──
// Single ticker driving Daily Anchor re-resolution AND Last Updated indicator.
// Re-ticks every 60s, on tab focus, and on manual refresh.

import { useCallback, useEffect, useState } from "react";

export interface RefreshCycle {
  /** Monotonically increasing tick — change forces consumers to re-resolve. */
  tick: number;
  /** Timestamp (ms) of the most recent tick. */
  lastUpdatedAt: number;
  /** Manual trigger — bumps tick and lastUpdatedAt. */
  refresh: () => void;
}

const SIXTY_SECONDS = 60_000;

export function useRefreshCycle(intervalMs: number = SIXTY_SECONDS): RefreshCycle {
  const [tick, setTick] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(() => Date.now());

  const refresh = useCallback(() => {
    setTick((t) => t + 1);
    setLastUpdatedAt(Date.now());
  }, []);

  // Interval
  useEffect(() => {
    const id = window.setInterval(refresh, intervalMs);
    return () => window.clearInterval(id);
  }, [refresh, intervalMs]);

  // Tab focus
  useEffect(() => {
    function onVis() {
      if (document.visibilityState === "visible") refresh();
    }
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refresh]);

  return { tick, lastUpdatedAt, refresh };
}

/** Format a timestamp as "Updated Xm ago" / "just now". */
export function formatLastUpdated(lastUpdatedAt: number, now: number = Date.now()): string {
  const diffSec = Math.max(0, Math.floor((now - lastUpdatedAt) / 1000));
  if (diffSec < 30) return "Updated just now";
  if (diffSec < 60) return "Updated <1m ago";
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `Updated ${m}m ago`;
  const h = Math.floor(m / 60);
  return `Updated ${h}h ago`;
}